import { LoggerService } from "@Front/Services/LoggerService/LoggerService";
import type { ProcessResponseOptions } from "../types";

type RetryReason = "304 Not Modified" | "Empty response body";
type RetryLogLevel = "debug" | "warn";

export async function retryWithoutCacheOnce<T>(params: {
	response: Response;
	request: () => Promise<Response>;
	processResponse: (
		response: Response,
		request: () => Promise<Response>,
		options?: ProcessResponseOptions,
	) => Promise<T>;
	options: ProcessResponseOptions;
	reason: RetryReason;
	logLevel: RetryLogLevel;
}): Promise<T> {
	const { response, request, processResponse, options, reason, logLevel } = params;
	const { forceNoCacheRequest, hasForcedNoCacheRetry } = options;
	if (!forceNoCacheRequest) {
		LoggerService.error(`❌ [BaseApiService] ${reason} but no forceNoCacheRequest available`, {
			url: response.url,
			status: response.status,
		});
		throw new Error(`${reason}: unable to re-fetch without cache`);
	}
	if (hasForcedNoCacheRetry) {
		LoggerService.error(`❌ [BaseApiService] ${reason} after no-cache retry, aborting loop`, {
			url: response.url,
			status: response.status,
		});
		throw new Error(`${reason} persists after no-cache retry`);
	}
	LoggerService[logLevel](`[BaseApiService] ${reason}, re-fetching without cache`, {
		url: response.url,
		status: response.status,
	});
	const freshResponse = await forceNoCacheRequest(true);
	return processResponse(freshResponse, request, {
		...options,
		hasForcedNoCacheRetry: true,
	});
}
